import type { DueKind } from '@ovo/shared'
import { DAY_FORMS, HOUR_FORMS, pl } from './plural.ts'

// Даты показываем в часовом поясе пользователя (me.settings.timezone), а не устройства.
// Без внешних библиотек: часовой пояс считаем через Intl.

const FALLBACK_TZ = 'Europe/Moscow'
const DAY_MS = 86_400_000

export function getDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TZ
  } catch {
    return FALLBACK_TZ
  }
}

let userTz = getDeviceTimezone()
const fmtCache = new Map<string, Intl.DateTimeFormat>()

export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function setUserTimezone(tz: string | null | undefined): void {
  if (!tz || tz === userTz || !isValidTimezone(tz)) return
  userTz = tz
  fmtCache.clear()
}

export function getUserTimezone(): string {
  return userTz
}

function fmt(key: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const cacheKey = `${userTz}|${key}`
  let f = fmtCache.get(cacheKey)
  if (!f) {
    f = new Intl.DateTimeFormat('ru-RU', { ...opts, timeZone: userTz })
    fmtCache.set(cacheKey, f)
  }
  return f
}

export interface ZonedParts {
  year: number
  /** 1..12 */
  month: number
  day: number
  hour: number
  minute: number
  second: number
  /** 1 = пн … 7 = вс */
  weekday: number
}

export function zonedParts(d: Date): ZonedParts {
  const parts = fmt('parts', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0)
  const year = get('year')
  const month = get('month')
  const day = get('day')
  const hour = get('hour') % 24
  const wd = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return { year, month, day, hour, minute: get('minute'), second: get('second'), weekday: wd === 0 ? 7 : wd }
}

/** Номер календарного дня в поясе пользователя — для сравнения «сегодня/завтра». */
export function dayNumber(d: Date): number {
  const p = zonedParts(d)
  return Date.UTC(p.year, p.month - 1, p.day) / DAY_MS
}

export function daysBetween(from: Date, to: Date): number {
  return dayNumber(to) - dayNumber(from)
}

function offsetMs(d: Date): number {
  const p = zonedParts(d)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(d.getTime() / 1000) * 1000
}

/** Момент времени по «настенным часам» пользователя. month — 1..12, переполнение дня нормализуется. */
export function zonedDate(year: number, month: number, day: number, hour = 0, minute = 0): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute)
  const off1 = offsetMs(new Date(guess))
  let ts = guess - off1
  const off2 = offsetMs(new Date(ts))
  if (off2 !== off1) ts = guess - off2
  return new Date(ts)
}

export function parseHhmm(s: string): [number, number] {
  const m = /^(\d{1,2}):(\d{2})/.exec(s)
  if (!m) return [18, 0]
  return [Math.min(23, Number(m[1])), Math.min(59, Number(m[2]))]
}

/** День base + offsetDays в указанное время (в поясе пользователя). */
export function atDayOffset(base: Date, offsetDays: number, hhmm: string): Date {
  const p = zonedParts(base)
  const [h, m] = parseHhmm(hhmm)
  const t = new Date(Date.UTC(p.year, p.month - 1, p.day + offsetDays))
  return zonedDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate(), h, m)
}

export function sameDay(a: Date, b: Date): boolean {
  return dayNumber(a) === dayNumber(b)
}

// ───────── Форматирование ─────────

export function formatTime(d: Date): string {
  return fmt('time', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)
}

export function formatWeekday(d: Date): string {
  return fmt('wd', { weekday: 'short' }).format(d).replace('.', '')
}

export function formatDayMonth(d: Date): string {
  return fmt('dm', { day: 'numeric', month: 'short' }).format(d).replace('.', '')
}

/** «сегодня», «завтра», «вчера», «пт, 9 окт» (+ год, если не текущий). */
export function formatDate(d: Date, now: Date = new Date()): string {
  const diff = daysBetween(now, d)
  if (diff === 0) return 'сегодня'
  if (diff === 1) return 'завтра'
  if (diff === -1) return 'вчера'
  const base = `${formatWeekday(d)}, ${formatDayMonth(d)}`
  return zonedParts(d).year === zonedParts(now).year ? base : `${base} ${zonedParts(d).year}`
}

/** «сегодня, 18:00», «пт, 9 окт, 18:00»; для dueKind=date — без времени. */
export function formatDue(iso: string, kind: DueKind = 'datetime', now: Date = new Date()): string {
  const d = new Date(iso)
  const date = formatDate(d, now)
  return kind === 'date' ? date : `${date}, ${formatTime(d)}`
}

/** Короткая дата диапазона отчёта: «28 сен». */
export function formatShortDate(iso: string): string {
  return formatDayMonth(new Date(iso))
}

/** Для ленты: «только что», «12 мин назад», «сегодня, 14:05», «пт, 9 окт, 10:00». */
export function formatMoment(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  const diffMin = Math.round((now.getTime() - d.getTime()) / 60_000)
  if (diffMin >= 0 && diffMin < 1) return 'только что'
  if (diffMin >= 1 && diffMin < 60) return `${diffMin} мин назад`
  return `${formatDate(d, now)}, ${formatTime(d)}`
}

/** Поддерживающая формулировка просрочки: «срок прошёл вчера», «срок прошёл 3 дня назад». */
export function overdueText(dueIso: string, now: Date = new Date()): string {
  const due = new Date(dueIso)
  const days = daysBetween(due, now)
  if (days <= 0) {
    const hours = Math.floor((now.getTime() - due.getTime()) / 3_600_000)
    return hours >= 1 ? `срок прошёл ${pl(hours, HOUR_FORMS)} назад` : 'срок только что прошёл'
  }
  if (days === 1) return 'срок прошёл вчера'
  return `срок прошёл ${pl(days, DAY_FORMS)} назад`
}

export function isPast(iso: string, now: Date = new Date()): boolean {
  return new Date(iso).getTime() < now.getTime()
}

// ───────── Нативные поля ввода ─────────

const pad = (n: number): string => String(n).padStart(2, '0')

/** ISO → значение <input type="datetime-local"> в поясе пользователя. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const p = zonedParts(new Date(iso))
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`
}

export function fromLocalInput(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!m) return null
  return zonedDate(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5])).toISOString()
}

export function toTimeInput(iso: string | null): string {
  if (!iso) return '18:00'
  const p = zonedParts(new Date(iso))
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** Тот же день, другое время. */
export function withTime(iso: string, hhmm: string): string {
  return atDayOffset(new Date(iso), 0, hhmm).toISOString()
}

// ───────── Группы «Мои задачи» ─────────

export type DueGroup = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none'

export const DUE_GROUP_ORDER: DueGroup[] = ['overdue', 'today', 'tomorrow', 'week', 'later', 'none']

export const DUE_GROUP_LABELS: Record<DueGroup, string> = {
  overdue: 'Срок прошёл',
  today: 'Сегодня',
  tomorrow: 'Завтра',
  week: 'Эта неделя',
  later: 'Позже',
  none: 'Без срока',
}

/** Конец «этой недели»: в выходные — следующая рабочая неделя целиком. */
function daysToWeekEnd(now: Date): number {
  const wd = zonedParts(now).weekday
  return wd >= 6 ? 7 - wd + 7 : 7 - wd
}

export function dueGroup(dueAt: string | null, isOverdue: boolean, now: Date = new Date()): DueGroup {
  if (!dueAt) return 'none'
  const due = new Date(dueAt)
  if (isOverdue || due.getTime() < now.getTime()) return 'overdue'
  const diff = daysBetween(now, due)
  if (diff <= 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff <= daysToWeekEnd(now)) return 'week'
  return 'later'
}

// ───────── Пресеты срока ─────────

export const DEFAULT_DUE_TIME = '18:00'

export function presetToday(now: Date = new Date()): Date {
  const p = zonedParts(now)
  if (p.hour < 17) return atDayOffset(now, 0, DEFAULT_DUE_TIME)
  if (p.hour >= 22) return atDayOffset(now, 0, '23:59')
  return atDayOffset(now, 0, `${pad(p.hour + 2)}:00`)
}

export function presetTomorrow(now: Date = new Date()): Date {
  return atDayOffset(now, 1, DEFAULT_DUE_TIME)
}

/** Ближайшая пятница (сегодня, если пятница и до 18:00). */
export function presetFriday(now: Date = new Date()): Date {
  const p = zonedParts(now)
  let delta = (5 - p.weekday + 7) % 7
  if (delta === 0 && p.hour >= 17) delta = 7
  return atDayOffset(now, delta, DEFAULT_DUE_TIME)
}

export function presetNextMonday(now: Date = new Date(), hhmm = '10:00'): Date {
  const p = zonedParts(now)
  const delta = (1 - p.weekday + 7) % 7 || 7
  return atDayOffset(now, delta, hhmm)
}

/** Сегодняшняя дата YYYY-MM-DD в поясе пользователя (для дат отсчёта правил). */
export function todayIso(now: Date = new Date()): string {
  const p = zonedParts(now)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}
